import type { Chat } from '@src/api/shared/community/chatApiClient';
import type { ChatReaction } from '@src/api/shared/community/chatReactionApiClient';
import ChatAttachmentList from '@src/features/shared/community/components/chat/chatList/ChatAttachmentList';
import PreviewOgp, {
  LinkPreviewData,
} from '@src/features/shared/community/components/chat/PreviewOgp';
import ReactionItem from '@src/features/shared/community/components/chat/ReactionItem';
import { useCopyHandler } from '@src/hooks/useCopyHandler';
import type { Attachment } from '@src/utils/idb';
import { handleMentionClick } from '@src/utils/mentionUtils';
import React from 'react';

type ChatContentProps = {
  chat: Chat;
  htmlWithMentions: string;
  preview: LinkPreviewData | null | undefined;
  attachments: Attachment[];
  reactions: ChatReaction[] | undefined;
  setMentionClickedChatId: (id: number | null) => void;
  displayTooltip: (duration: number) => void;
  isHtmlEmpty: (html: string) => boolean;
  isOwner?: boolean;
  onTogglePreview?: (hidePreview: boolean) => void;
};

const ChatContent: React.FC<ChatContentProps> = ({
  chat,
  htmlWithMentions,
  preview,
  attachments,
  reactions,
  setMentionClickedChatId,
  displayTooltip,
  isHtmlEmpty,
  isOwner,
  onTogglePreview,
}) => {
  const contentRef = useCopyHandler<HTMLDivElement>();

  const shouldShowPreview = preview && !chat.is_link_preview_hidden;

  return (
    <div className='min-w-0 whitespace-pre-wrap pl-10 text-sm text-[#000] md:pl-0'>
      {!isHtmlEmpty(htmlWithMentions) ? (
        <div
          ref={contentRef}
          className='tiptap-content break-all pl-1 lg:pl-0'
          dangerouslySetInnerHTML={{ __html: htmlWithMentions }}
          onClick={(e) => handleMentionClick(e, chat.id, setMentionClickedChatId, displayTooltip)}
        />
      ) : null}

      {shouldShowPreview && (
        <PreviewOgp
          data={preview}
          isPinnedChat={false}
          canHide={isOwner}
          onHide={() => onTogglePreview?.(true)}
        />
      )}

      <ChatAttachmentList
        attachments={attachments}
        chat={chat}
        hasMessageContent={!isHtmlEmpty(htmlWithMentions)}
      />

      <div className='no-scrollbar mt-2 flex flex-nowrap gap-1 overflow-x-auto pb-1 pl-1 lg:pl-0'>
        {reactions?.map((reaction: ChatReaction, index: number) => (
          <ReactionItem
            key={`${reaction.emoji}-${index}`}
            chatId={chat.id}
            reaction={reaction}
            allReactions={reactions}
          />
        ))}
      </div>
    </div>
  );
};

export default ChatContent;
